
import { Component, Vue, Watch, Prop, InjectReactive } from "vue-property-decorator"
import { namespace, Getter, Mutation, Action } from "vuex-class"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import { Noticeboard, Note, NoteWithUser } from "@/types/noticeboard.ts"

import SkeletonLoader from "../SkeletonLoader.vue"
import vSelect from "vue-select"

import apiNoticeboard from "@/services/api_Noticeboard"

import Guid from "@/scripts/guid"
import SaveNoticeboardModal from "@/components/Dashboard/SaveNoticeboardModal.vue"
import User, { CurrentUser } from "@/types/user"
import _ from "lodash"
import TextAreaExpand from "../Fields/TextAreaExpand.vue"
import { formatDateMixin } from "@/mixins/formatDateMixin"
import moment from "moment"
import UserImage from "../UserImage.vue"

@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"skeleton": SkeletonLoader,
		"v-select": vSelect,
		"save-noticeboard-modal": SaveNoticeboardModal,
		"text-area-expand": TextAreaExpand,
		"user-image": UserImage
	}
})
export default class NoticeboardDisplay extends Vue {
	$refs!: {
		saveNoticeboardModal: SaveNoticeboardModal
	}
	@Prop()
	value: any;

	@Prop()
	edit!: boolean

	@Getter("getMe") public me!: CurrentUser;

	noticeboards: Noticeboard[] = []
	selectedNoticeboard: Noticeboard | null = null;
	selectedNoticeboardId: string = "";

	notesWithUsers: NoteWithUser[] = []

	loading: boolean = true;

	errorLoading: boolean = false

	reloadResultsKey: string | null = null;

	noteText: string | null = null;

	public async mounted () {
		this.noticeboards = await apiNoticeboard.noticeboardList()

		if (this.value && this.value.selectedNoticeboardId) {
			this.selectedNoticeboardId = this.value.selectedNoticeboardId
			this.selectedNoticeboard = this.noticeboards.find(c => c.id === this.selectedNoticeboardId) || null

			await this.refreshNotesWithUsers()
			this.reSortNotesWithUsers()
		}
		this.reloadResultsKey = Guid.newGuid()
		this.loading = false
	}

	private addNoticeboard () {
		this.$refs.saveNoticeboardModal.openForAdding()
	}

	private editNoticeboard () {
		this.$refs.saveNoticeboardModal.openForEditing(this.selectedNoticeboardId)
	}

	private async deleteNoticeboard () {
		if (this.selectedNoticeboardId) {
			await apiNoticeboard.noticeboardDelete(this.selectedNoticeboardId)
			this.noticeboards = await apiNoticeboard.noticeboardList()
			if (this.noticeboards.length > 0) {
				await this.selectNoticeboard(this.noticeboards[0].id!)
			} else {
				this.unselectNoticeboard()
			}
		}
	}

	private async onNoticeboardSaved (newNoticeboardId: string) {
		this.noticeboards = await apiNoticeboard.noticeboardList()
		await this.selectNoticeboard(newNoticeboardId)
	}

	private async refreshNotesWithUsers () {
		this.notesWithUsers = await apiNoticeboard.noteList(this.selectedNoticeboardId)
	}

	private reSortNotesWithUsers () {
		if (this.sortByProperty && this.sortByDirection) {
			this.notesWithUsers = _.orderBy(this.notesWithUsers, this.sortByProperty, this.sortByDirection as any)
		}
	}

	public async unselectNoticeboard () {
		this.selectedNoticeboardId = ""
		this.selectedNoticeboard = null
		Vue.delete(this.value, "selectedNoticeboardId")
		this.$emit("input", { selectedNoticeboardId: null })
		this.notesWithUsers = []
		this.noteText = ""
		this.noticeboards = []
	}

	public async selectNoticeboard (selectedNoticeboardId: string) {
		this.selectedNoticeboardId = selectedNoticeboardId
		this.selectedNoticeboard = this.noticeboards.find(c => c.id === selectedNoticeboardId) || null

		this.$emit("input", { selectedNoticeboardId: selectedNoticeboardId })
		await this.refreshNotesWithUsers()
		this.reSortNotesWithUsers()
	}

	private setSortDirection (newSortByProperty: string) {
		if (this.sortByProperty === newSortByProperty && this.sortByDirection) {
			const newSortDirection = this.sortByDirection === "asc" ? "desc" : "asc"
			this.sortByDirection = newSortDirection
		} else {
			this.sortByDirection = "asc"
		}
	}

	sortByProperty: string = "";
	sortByDirection: string = "";
	clickedNoteId: string = "";

	private sortByUser () {
		this.setSortDirection("user.name")
		this.sortByProperty = "user.name"
		this.reSortNotesWithUsers()
	}

	private sortByNote () {
		this.setSortDirection("note.text")
		this.sortByProperty = "note.text"
		this.reSortNotesWithUsers()
	}

	private sortByDate () {
		this.setSortDirection("note.postedAt")
		this.sortByProperty = "note.postedAt"
		this.reSortNotesWithUsers()
	}

	private sortByLastUpdated () {
		this.setSortDirection("note.lastUpdatedAt")
		this.sortByProperty = "note.lastUpdatedAt"
		this.reSortNotesWithUsers()
	}

	private async saveNote () {
		if (!this.selectedNoticeboardId) {
			return
		}

		let noteToSave: Note
		if (this.clickedNoteId === "") { // new note
			noteToSave = {
				text: this.noteText || "",
				noticeboardId: this.selectedNoticeboardId
			}
		} else {
			noteToSave = this.notesWithUsers.find(c => c.note.id === this.clickedNoteId)!.note
			noteToSave.text = this.noteText || ""
		}

		await apiNoticeboard.noteSave(noteToSave)
		await this.refreshNotesWithUsers()
		this.reSortNotesWithUsers()

		this.clickedNoteId = ""
		this.noteText = null
	}

	private async deleteNote () {
		if (!this.selectedNoticeboardId) {
			return
		}

		if (this.clickedNoteId === "") {
			return
		}

		await apiNoticeboard.noteDelete(this.clickedNoteId)
		await this.refreshNotesWithUsers()
		this.reSortNotesWithUsers()

		this.clickedNoteId = ""
		this.noteText = null
	}

	private convertToLocalTime (noteDate: Date): string {
		return noteDate ? moment(noteDate).format("l LTS") : ""
	}

	private getAriaSortAttributeValue (sortByProperty: string): string {
		return (this.sortByProperty === sortByProperty ? (this.sortByDirection === "asc" ? "ascending" : "descending") : "none")
	}

	private noteWasClicked (noteId: string): boolean {
		return noteId === this.clickedNoteId
	}

	private onNoteClick (noteId: string) {
		if (this.clickedNoteId === noteId) { // if highlighted row is clicked again, unhighlight it
			this.clickedNoteId = ""
			this.noteText = ""
			return
		}
		this.clickedNoteId = noteId

		const clickedNote = this.notesWithUsers.find(c => c.note.id === noteId)!
		this.noteText = clickedNote.note.text
	}
}

